/**
 * Traffic Tracker — a client-side instrumentation tool for verifying that the
 * caching / data-version layer behaves as intended.
 *
 * It records, per page URL, every backend hit and cache operation while the
 * user is "recording" (toggled from the profile modal + a floating pill). A
 * saved session is a snapshot of that event log, viewable from the admin
 * Analytics tab.
 *
 * Design notes:
 *  - Framework-agnostic singleton so the instrumentation points (RTK
 *    baseQuery, useDataVersionCheck, localStore cache fns) can emit without a
 *    React tree. A `useTrafficTracker` hook (bottom of file) exposes it to UI
 *    via useSyncExternalStore.
 *  - The active session persists to localStorage on every event so a mid-
 *    recording PAGE REFRESH (the core caching test) does not lose state.
 *  - Keys use the `analytics_tracker.` prefix on purpose: the admin
 *    "Refresh Data" action wipes all `pc_*` / `dataVersion:*` keys, and
 *    tracked sessions should survive that.
 */

import { readJSON, writeJSON } from "@/utils/safeStorage";

// newly-approved items merged into a cached list

// localStorage

/** One measured page load in page-load mode. */

/** The index pages measured by page-load mode, in suggested visit order. */
export const PAGE_LOAD_TARGETS = ["/", "/organizations", "/events", "/regulations", "/articles", "/professionals"];
const K_ENABLED = "analytics_tracker.enabled";
const K_ACTIVE = "analytics_tracker.active";
const K_SESSIONS = "analytics_tracker.sessions";
const K_DAILY = "analytics_tracker.daily";
const MAX_ACTIVE_EVENTS = 5000; // safety cap so a runaway session can't bloat storage
const MAX_SAVED_SESSIONS = 50; // keep the newest N saved sessions; drop the oldest so localStorage can't grow unbounded

function zeroTotals() {
  return {
    backend: 0,
    cacheReads: 0,
    cacheHits: 0,
    cacheMisses: 0,
    cacheWrites: 0,
    versionChecks: 0,
    merges: 0
  };
}
function addToTotals(t, e) {
  switch (e.kind) {
    case "backend":
      t.backend += 1;
      break;
    case "cache-read":
      t.cacheReads += 1;
      if (e.hit) t.cacheHits += 1;else t.cacheMisses += 1;
      break;
    case "cache-write":
      t.cacheWrites += 1;
      break;
    case "version-check":
      t.versionChecks += 1;
      break;
    case "merge":
      t.merges += 1;
      break;
  }
}
function totalsFrom(events) {
  const t = zeroTotals();
  for (const e of events) addToTotals(t, e);
  return t;
}
function bucketByUrl(events) {
  const map = new Map();
  for (const e of events) {
    // Normalized so "/", "/home" and "/home/" group as one page.
    const key = normalizeNavPath(e.url);
    let b = map.get(key);
    if (!b) {
      b = {
        url: key,
        totals: zeroTotals(),
        events: []
      };
      map.set(key, b);
    }
    b.events.push(e);
    addToTotals(b.totals, e);
  }
  // Most-active URL first.
  return [...map.values()].sort((a, b) => b.events.length - a.events.length);
}
const isBrowser = typeof window !== "undefined";
function todayStr() {
  // Local calendar day.
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
const PAGE_LOAD_SETTLE_MS = 1500;

/** Entity each measured index page is "about". "/" touches several
 *  entities, so home measurements accept every event (null). */
const TARGET_ENTITY = {
  "/": null,
  "/organizations": "organizations",
  "/events": "events",
  "/regulations": "regulations",
  "/articles": "articles",
  "/professionals": "professionals"
};

/** Parse the entity out of a cache key like "index:organizations" or
 *  "detail:events:some-slug". */
export function entityFromCacheKey(key) {
  const m = /^(?:index|detail|counts):([a-z]+)/.exec(key);
  if (m) return m[1];
  if (key.startsWith("promotions:")) return "promotions";
  return undefined;
}

/**
 * Normalize a pathname for page-load target matching: strip trailing
 * slashes WITHOUT collapsing "/" itself to "" (the homepage's events would
 * otherwise never match their measurement window), and fold the "/home"
 * alias into "/". Both notePageNav and emit MUST use this — a one-sided
 * normalization mis-phased the homepage's initial load as "cached" and
 * permanently stuck page-load sessions at 11/12.
 */
function normalizeNavPath(pathname) {
  const clean = pathname.replace(/\/+$/, "") || "/";
  return clean === "/home" ? "/" : clean;
}
function connectionInfo() {
  if (!isBrowser) return {
    downlinkMbps: null,
    effectiveType: null
  };
  const conn = navigator.connection;
  return {
    downlinkMbps: typeof conn?.downlink === "number" ? conn.downlink : null,
    effectiveType: conn?.effectiveType ?? null
  };
}
class TrafficTracker {
  enabled = false;
  active = null;
  saved = [];
  daily = {
    date: todayStr(),
    totals: zeroTotals()
  };
  listeners = new Set();
  seq = 0;
  // Page-load mode: the nav being measured + its settle timer.
  navUrl = null;
  navStartedAt = 0;
  navLastEventAt = 0;
  /** Stamped when the router finishes the transition (see notePageNavDone). */
  navCompletedAt = 0;
  navBackendHits = 0;
  settleTimer = null;
  lastPageLoadSessionId = null;
  constructor() {
    if (isBrowser) {
      this.enabled = readJSON(K_ENABLED, false);
      this.active = readJSON(K_ACTIVE, null);
      this.saved = readJSON(K_SESSIONS, []);
      const storedDaily = readJSON(K_DAILY, null);
      if (storedDaily && storedDaily.date === todayStr()) {
        this.daily = {
          date: storedDaily.date,
          totals: {
            ...zeroTotals(),
            ...storedDaily.totals
          }
        };
      }
      // Self-heal: if tracking is enabled but no active session
      // survived (fresh enable, or corrupted/cleared storage), start a
      // recording session so capture actually begins.
      if (this.enabled && !this.active) {
        this.active = {
          id: this.nextId("sess"),
          startedAt: Date.now(),
          recording: true,
          events: []
        };
        writeJSON(K_ACTIVE, this.active);
      }
    }
    this.snapshot = this.buildSnapshot();
  }

  // ── Read side (for useSyncExternalStore) ──
  getSnapshot = () => this.snapshot;
  subscribe = listener => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };
  buildSnapshot() {
    const events = this.active?.events ?? [];
    // Return copies of internally-held references (activeEvents, dailyTotals,
    // savedSessions) so a consumer reading the snapshot via
    // useSyncExternalStore can't mutate tracker state by sorting/pushing.
    // (totals/byUrl are already freshly built each call.)
    return {
      enabled: this.enabled,
      recording: !!this.active?.recording,
      activeStartedAt: this.active?.startedAt ?? null,
      activeEvents: [...events],
      totals: totalsFrom(events),
      byUrl: bucketByUrl(events),
      dailyTotals: {
        ...this.daily.totals
      },
      dailyDate: this.daily.date,
      savedSessions: [...this.saved],
      pageLoadMode: !!this.active?.pageLoadMode,
      pageLoads: [...(this.active?.pageLoads ?? [])],
      pageLoadComplete: this.isPageLoadComplete(),
      lastPageLoadSessionId: this.lastPageLoadSessionId
    };
  }
  isPageLoadComplete() {
    const loads = this.active?.pageLoads ?? [];
    return PAGE_LOAD_TARGETS.every(url => loads.some(l => l.url === url && l.phase === "initial") && loads.some(l => l.url === url && l.phase === "cached"));
  }
  notify() {
    this.snapshot = this.buildSnapshot();
    this.listeners.forEach(l => l());
  }
  nextId(prefix) {
    this.seq += 1;
    return `${prefix}_${Date.now().toString(36)}_${this.seq}`;
  }

  // ── Controls ──
  isCapturing() {
    return this.enabled && !!this.active?.recording;
  }
  setEnabled(on) {
    this.enabled = on;
    writeJSON(K_ENABLED, on);
    if (on && !this.active) {
      this.active = {
        id: this.nextId("sess"),
        startedAt: Date.now(),
        recording: true,
        events: []
      };
      writeJSON(K_ACTIVE, this.active);
    }
    this.notify();
  }
  setRecording(on) {
    if (!this.active) {
      this.active = {
        id: this.nextId("sess"),
        startedAt: Date.now(),
        recording: on,
        events: []
      };
    } else {
      this.active.recording = on;
    }
    writeJSON(K_ACTIVE, this.active);
    this.notify();
  }

  /**
   * Toggle page-load capture mode. The caller (pill) clears the data
   * caches first so every target page's first visit is a true initial
   * load. Turning the mode on resets the active session.
   */
  setPageLoadMode(on) {
    this.finalizeNav();
    // A fresh run shouldn't pair its 0/12 counter with the previous
    // run's View Result link.
    if (on) this.lastPageLoadSessionId = null;
    this.active = {
      id: this.nextId("sess"),
      startedAt: Date.now(),
      recording: on ? true : !!this.active?.recording,
      events: [],
      pageLoadMode: on,
      pageLoads: []
    };
    writeJSON(K_ACTIVE, this.active);
    this.notify();
  }

  /**
   * Router hook: a page-load-mode capture starts when the app navigates
   * to one of the target index pages. Any in-flight measurement is
   * finalized first.
   */
  notePageNav(pathname) {
    if (!this.isCapturing() || !this.active?.pageLoadMode) return;
    this.finalizeNav();
    // pages/index.tsx re-exports the homepage, so "/" and "/home" are the
    // same page — measure both under the "/" target.
    const clean = normalizeNavPath(pathname);
    if (!PAGE_LOAD_TARGETS.includes(clean)) return;
    this.navUrl = clean;
    this.navStartedAt = Date.now();
    this.navLastEventAt = 0;
    this.navCompletedAt = 0;
    this.navBackendHits = 0;
  }

  /**
   * Router hook: the transition finished. Stamping the completion time lets
   * a fully-cached page still be measured — it reads straight from local
   * storage during mount and can emit no traffic events at all, which would
   * otherwise leave its "cached" phase permanently uncaptured.
   */
  notePageNavDone() {
    if (!this.navUrl || !this.active?.pageLoadMode) return;
    this.navCompletedAt = Date.now();
  }

  /** Close out the in-flight page measurement, if any. */
  finalizeNav() {
    if (this.settleTimer) {
      clearTimeout(this.settleTimer);
      this.settleTimer = null;
    }
    if (!this.navUrl || !this.active?.pageLoadMode) {
      this.navUrl = null;
      return;
    }
    // A fully-cached page can settle without emitting a single tracked
    // event, so fall back to when the router finished the transition.
    const endedAt = this.navLastEventAt || this.navCompletedAt;
    if (endedAt > 0) {
      const phase = this.navBackendHits > 0 ? "initial" : "cached";
      const loads = this.active.pageLoads ?? (this.active.pageLoads = []);
      // Keep the FIRST capture of each url+phase — re-visits while a
      // phase is already recorded don't overwrite it.
      if (!loads.some(l => l.url === this.navUrl && l.phase === phase)) {
        const conn = connectionInfo();
        loads.push({
          url: this.navUrl,
          phase,
          durationMs: endedAt - this.navStartedAt,
          backendHits: this.navBackendHits,
          downlinkMbps: conn.downlinkMbps,
          effectiveType: conn.effectiveType,
          at: Date.now()
        });
        writeJSON(K_ACTIVE, this.active);
        // All pages captured in both phases → auto-pause and save.
        if (this.isPageLoadComplete()) {
          this.active.recording = false;
          writeJSON(K_ACTIVE, this.active);
          const completedLoads = [...loads];
          const saved = this.saveSession(`Page loads · ${new Date().toLocaleString()}`);
          this.lastPageLoadSessionId = saved?.id ?? null;
          // saveSession resets the active session, which would
          // uncheck the mode, zero the 12/12 counter, and hide
          // View Result the instant the capture completed. Keep
          // the finished capture visible (paused) until the user
          // leaves page-load mode.
          if (this.active) {
            this.active.pageLoadMode = true;
            this.active.pageLoads = completedLoads;
            writeJSON(K_ACTIVE, this.active);
          }
        }
      }
    }
    this.navUrl = null;
    this.navBackendHits = 0;
    this.navCompletedAt = 0;
    this.notify();
  }

  /** Clear the in-progress session's events (keeps recording state). */
  clearActive() {
    if (!this.active) return;
    this.active.events = [];
    writeJSON(K_ACTIVE, this.active);
    this.notify();
  }

  /**
   * Pause recording and persist the current session to the saved list.
   * No-op (returns null) if there is nothing captured.
   */
  saveSession(name) {
    if (!this.active || this.active.events.length === 0) return null;
    const events = this.active.events;
    const session = {
      id: this.nextId("saved"),
      name: name?.trim() || `Session · ${events.length} events · ${new Date(this.active.startedAt).toLocaleString()}`,
      startedAt: this.active.startedAt,
      savedAt: Date.now(),
      events,
      totals: totalsFrom(events),
      byUrl: bucketByUrl(events),
      ...(this.active.pageLoads?.length ? {
        pageLoads: [...this.active.pageLoads]
      } : {})
    };
    // Prepend the newest and cap the list so saved sessions can't grow
    // unbounded and blow the localStorage quota.
    this.saved = [session, ...this.saved].slice(0, MAX_SAVED_SESSIONS);
    writeJSON(K_SESSIONS, this.saved);
    // Reset the active session and pause.
    this.active = {
      id: this.nextId("sess"),
      startedAt: Date.now(),
      recording: false,
      events: []
    };
    writeJSON(K_ACTIVE, this.active);
    this.notify();
    return session;
  }
  deleteSession(id) {
    this.saved = this.saved.filter(s => s.id !== id);
    writeJSON(K_SESSIONS, this.saved);
    this.notify();
  }
  getSavedSessions() {
    return this.saved;
  }

  // ── Emit (called by instrumentation points) ──
  emit(e) {
    if (!this.isCapturing()) return;
    const url = e.url ?? (isBrowser ? window.location.pathname : "(server)") ?? "(unknown)";
    const event = {
      ...e,
      id: this.nextId("ev"),
      ts: Date.now(),
      url
    };

    // Roll the daily bucket over at midnight.
    const today = todayStr();
    if (this.daily.date !== today) {
      this.daily = {
        date: today,
        totals: zeroTotals()
      };
    }
    addToTotals(this.daily.totals, event);
    writeJSON(K_DAILY, this.daily);
    if (this.active) {
      this.active.events.push(event);
      if (this.active.events.length > MAX_ACTIVE_EVENTS) {
        this.active.events.shift();
      }
      writeJSON(K_ACTIVE, this.active);
    }

    // Page-load mode: events for the measured page extend its window;
    // silence for PAGE_LOAD_SETTLE_MS finalizes the measurement.
    // Cross-entity cache/version events (localStore warm-up reading
    // every cache type, Navigation checking all entities) must not
    // extend the measurement window — they inflated "cached" numbers
    // by resetting the settle timer with unrelated bookkeeping.
    // Backend hits and merges always count: they are real page work.
    const targetEntity = this.navUrl ? TARGET_ENTITY[this.navUrl] : undefined;
    const isCrossEntity = targetEntity != null && (event.kind === "cache-read" || event.kind === "cache-write" || event.kind === "version-check") && !!event.entity && event.entity !== targetEntity;
    if (this.active?.pageLoadMode && this.navUrl && !isCrossEntity && normalizeNavPath(event.url) === this.navUrl) {
      this.navLastEventAt = event.ts;
      if (event.kind === "backend") this.navBackendHits += 1;
      if (this.settleTimer) clearTimeout(this.settleTimer);
      this.settleTimer = setTimeout(() => this.finalizeNav(), PAGE_LOAD_SETTLE_MS);
    }
    this.notify();
  }
}
export const trafficTracker = new TrafficTracker();

// ── Convenience emit helpers used by the instrumentation points ──
// Each is a cheap no-op when not capturing (guarded inside emit).

export function trackBackendHit(args) {
  const method = (args.method ?? "GET").toUpperCase();
  trafficTracker.emit({
    kind: "backend",
    label: `${method} ${args.endpoint}`,
    status: args.status,
    durationMs: args.durationMs,
    source: args.source ?? "rtk",
    detail: args.detail
  });
}
export function trackCacheRead(key, hit, source = "storage") {
  trafficTracker.emit({
    kind: "cache-read",
    label: `cache ${hit ? "HIT" : "MISS"} · ${key}`,
    hit,
    source,
    entity: entityFromCacheKey(key)
  });
}
export function trackCacheWrite(key, count, source = "storage") {
  trafficTracker.emit({
    kind: "cache-write",
    label: count != null ? `cache WRITE · ${key} (${count} item${count === 1 ? "" : "s"})` : `cache WRITE · ${key}`,
    source,
    entity: entityFromCacheKey(key),
    detail: count != null ? {
      count
    } : undefined
  });
}
export function trackVersionCheck(args) {
  trafficTracker.emit({
    kind: "version-check",
    label: `version ${args.entity}: stored=${args.storedVersion ?? "∅"} server=${args.serverVersion ?? "∅"}${args.stale ? " → STALE" : " (fresh)"}`,
    source: args.source ?? "version-fetch",
    entity: args.entity,
    detail: {
      entity: args.entity,
      storedVersion: args.storedVersion,
      serverVersion: args.serverVersion,
      stale: args.stale
    }
  });
}
export function trackMerge(entity, added) {
  trafficTracker.emit({
    kind: "merge",
    label: `merge ${entity}: +${added} newly-approved`,
    detail: {
      entity,
      added
    }
  });
}